import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Login automático via query string: /login/auto?email=...&senha=...
 * O parâmetro `senha` deve ser o hash bcrypt retornado em adminUser.senha (provisionamento),
 * URL-encoded (ex.: caracteres $ como %24).
 */
export function AutoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithStoredHash, isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (isLoading) return;

    // Após login bem-sucedido, o App passa a renderizar rotas autenticadas; a rota
    // `/login/auto` redireciona para `/contatos` (evita corrida com setState).
    if (isAuthenticated) {
      navigate('/contatos', { replace: true });
      return;
    }

    const email = searchParams.get('email');
    const senha = searchParams.get('senha');

    if (!email || !senha) {
      setError('Informe email e senha (hash bcrypt) na URL. Ex.: /login/auto?email=a@b.com&senha=%242a%24...');
      return;
    }

    if (attempted.current) return;
    attempted.current = true;

    void loginWithStoredHash(email, senha).then((ok) => {
      if (!ok) {
        setError('Não foi possível fazer login automático. Verifique o e-mail e o hash.');
      }
    });
  }, [isLoading, isAuthenticated, searchParams, loginWithStoredHash, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg px-8 py-10 max-w-md w-full text-center">
        {!error ? (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-gray-700">Entrando automaticamente…</p>
          </>
        ) : (
          <>
            <p className="text-red-600 mb-6">{error}</p>
            <Link
              to="/login"
              className="inline-block px-4 py-2 rounded-xl text-white font-medium"
              style={{ background: 'linear-gradient(135deg, #47A7F6 0%, #3694e8 100%)' }}
            >
              Ir para o login manual
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
